import { ParsedUrlQuery, stringify } from "querystring";

import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { worksheets } from "@/search/Langston/worksheets";

// The worksheets filter bypasses the API and uses a hardcoded list, so we
// derive the count directly from that list instead of using a magic number.
export const WORKSHEETS_RESULT_COUNT = worksheets.length;

export const getTotalResultCount = (
  isDisabledFilters: boolean,
  totalResultCountFromResponse: number | undefined,
): number => {
  return isDisabledFilters
    ? WORKSHEETS_RESULT_COUNT
    : totalResultCountFromResponse || 0;
};

export const formatResultCountMessage = (count: number): string =>
  `${Intl.NumberFormat("en-US").format(count)} most relevant results`;

const UNIVERSAL_EMBED_CONTENT_TYPES = ["ARTICLE", "BUNDLE"];

export const initializeSearchParams = (
  query: ParsedUrlQuery,
  isUniversalEmbed = false,
) => {
  const searchParams = new URLSearchParams(stringify(query));
  const validSearchParams = Object.keys(LangstonParameterSchema);
  const allSearchParams = [...searchParams.entries()];

  // Only load valid search parameters
  allSearchParams.forEach(([paramName]) => {
    if (!validSearchParams.includes(paramName)) {
      searchParams.delete(paramName);
    }
  });

  // Set default filters
  const isSortByParamValueValid = LangstonParameterSchema.sort.values
    .map(({ value }) => value)
    .includes(searchParams.get(LangstonParameterSchema.sort.key) || "");
  if (!isSortByParamValueValid) {
    searchParams.set(
      LangstonParameterSchema.sort.key,
      LangstonParameterSchema.sort.default,
    );
  }

  const isLanguagesParamValueValid = LangstonParameterSchema.languages.values
    .map(({ value }) => value)
    .includes(searchParams.get(LangstonParameterSchema.languages.key) || "");
  if (!isLanguagesParamValueValid) {
    searchParams.set(
      LangstonParameterSchema.languages.key,
      LangstonParameterSchema.languages.default,
    );
  }

  const hasPageParam = searchParams.get(LangstonParameterSchema.page.key);
  if (!hasPageParam) {
    searchParams.set(
      LangstonParameterSchema.page.key,
      LangstonParameterSchema.page.default.toString(),
    );
  }

  if (isUniversalEmbed) {
    searchParams.delete(LangstonParameterSchema.content_types.key);
    UNIVERSAL_EMBED_CONTENT_TYPES.forEach((type) =>
      searchParams.append(LangstonParameterSchema.content_types.key, type),
    );
  }

  return searchParams;
};

export const getLangstonUrlParams = (params: URLSearchParams): string => {
  const promptKey = LangstonParameterSchema.prompt.key;
  const needleKey = LangstonParameterSchema.needle.key;

  const cloned = new URLSearchParams(params.toString());

  const hasPrompt = cloned.has(promptKey);
  const hasNeedle = cloned.has(needleKey);

  // Clean up any non-Langston params.
  cloned.delete(promptKey);

  // If we have a prompt but no needle yet, we're in the
  // "Luna is fetching" state. Don't trigger Langston.
  if (hasPrompt && !hasNeedle) {
    return "";
  }

  return cloned.toString();
};
